import { useCallback, useEffect, useLayoutEffect, useRef, useState } from "react";
import { CellWrap } from "@/components/board/cells/CellWrap";
import { Icon } from "@/components/ui/Icon";
import { TicketAgeChip, TicketIdChip } from "@/components/board/TicketChips";
import { t } from "@/lib/strings";
import type { UUID } from "@/lib/types";

interface TitleCellProps {
  value: string;
  rowH?: number;
  rowId: UUID;
  boardId: UUID;
  columnDefId: UUID;
  isLast?: boolean;
  autoFocus?: boolean;
  /** Ticketing: human ID ("IT-42") shown leading the title; age chip trailing. */
  ticketIdLabel?: string | null;
  ticketCreatedAt?: string | null;
  ticketResolvedAt?: string | null;
  /** Number of comments on the row — shows a small bubble badge when > 0. */
  commentCount?: number;
  /** A sub-item (depth > 0) — rendered lighter/muted so it reads as subordinate. */
  isSubitem?: boolean;
  onOpen: () => void;
  onPatch: (value: string) => void;
  onCreateNext: (afterRowId: UUID) => void;
}

const MOBILE_BREAKPOINT_PX = 640;

function isMobile(): boolean {
  if (typeof window === "undefined" || !window.matchMedia) return false;
  return window.matchMedia(`(max-width: ${MOBILE_BREAKPOINT_PX}px)`).matches;
}

export function TitleCell({
  value,
  rowH = 44,
  rowId,
  autoFocus = false,
  ticketIdLabel,
  ticketCreatedAt,
  ticketResolvedAt,
  commentCount = 0,
  isSubitem = false,
  onOpen,
  onPatch,
  onCreateNext,
}: TitleCellProps) {
  const [hover, setHover] = useState(false);
  const [editing, setEditing] = useState(autoFocus);
  const [draft, setDraft] = useState(value);
  const inputRef = useRef<HTMLInputElement | null>(null);
  const baselineRef = useRef(value);
  const revertingRef = useRef(false);

  useEffect(() => {
    if (!editing) setDraft(value);
  }, [value, editing]);

  useLayoutEffect(() => {
    if (!editing) return;
    const el = inputRef.current;
    if (!el) return;
    el.focus();
    const end = el.value.length;
    try {
      el.setSelectionRange(end, end);
    } catch {
      /* some input types don't support setSelectionRange */
    }
    // A freshly-created row (autoFocus) can be off-screen — e.g. appended to a
    // group at the bottom. Scroll it into view so the cursor is visible.
    if (autoFocus) el.scrollIntoView({ block: "center", behavior: "smooth" });
  }, [editing, autoFocus]);

  const enterEdit = useCallback(() => {
    if (editing) return;
    baselineRef.current = value;
    setDraft(value);
    setEditing(true);
  }, [editing, value]);

  // A just-created row sets autoFocus AFTER this cell has already mounted (the id
  // only exists once the server responds), so the initial useState(autoFocus) is
  // false. React to autoFocus flipping true → enter edit so the cursor lands here.
  useEffect(() => {
    if (autoFocus) enterEdit();
  }, [autoFocus, enterEdit]);

  const commit = useCallback((): boolean => {
    const next = draft;
    const changed = next !== baselineRef.current;
    if (changed) onPatch(next);
    baselineRef.current = next;
    return changed;
  }, [draft, onPatch]);

  const handleKeyDown = useCallback(
    (e: React.KeyboardEvent<HTMLInputElement>) => {
      if (e.key === "Escape") {
        e.preventDefault();
        e.stopPropagation();
        revertingRef.current = true;
        setDraft(baselineRef.current);
        setEditing(false);
        return;
      }
      if (e.key === "Enter" && e.shiftKey) {
        e.preventDefault();
        return;
      }
      if (e.key === "Enter" && (e.metaKey || e.ctrlKey)) {
        e.preventDefault();
        commit();
        setEditing(false);
        onOpen();
        return;
      }
      if (e.key === "Enter") {
        e.preventDefault();
        commit();
        setEditing(false);
        if (isMobile()) {
          inputRef.current?.blur();
          return;
        }
        onCreateNext(rowId);
        return;
      }
      if (e.key === "Tab") {
        commit();
        setEditing(false);
        return;
      }
    },
    [commit, onCreateNext, onOpen, rowId],
  );

  const handleBlur = useCallback(() => {
    if (revertingRef.current) {
      revertingRef.current = false;
      return;
    }
    if (editing) {
      commit();
      setEditing(false);
    }
  }, [commit, editing]);

  if (editing) {
    return (
      <CellWrap rowH={rowH} interactive={false}>
        <input
          ref={inputRef}
          name="cell-title"
          autoComplete="off"
          value={draft}
          onChange={(e) => setDraft(e.target.value)}
          onKeyDown={handleKeyDown}
          onBlur={handleBlur}
          onClick={(e) => e.stopPropagation()}
          onMouseDown={(e) => e.stopPropagation()}
          spellCheck={false}
          inputMode="text"
          style={{
            width: "100%",
            height: "100%",
            border: "none",
            outline: "none",
            background: "transparent",
            color: "var(--ink-1)",
            fontSize: 13.5,
            fontWeight: 500,
            fontFamily: "Inter Variable, Inter, system-ui, sans-serif",
            padding: 0,
            margin: 0,
          }}
        />
      </CellWrap>
    );
  }

  // Clicking a row's title opens the detail panel — renaming lives there, not
  // inline (an inline-edit-on-click kept firing when people just wanted to open
  // the row). Empty titles keep whole-cell click-to-edit so naming a freshly
  // created row stays one click (also covered by autoFocus on row create).
  const hasTitle = value.trim().length > 0;

  return (
    <CellWrap rowH={rowH} onClick={hasTitle ? onOpen : enterEdit}>
      <div
        onMouseEnter={() => setHover(true)}
        onMouseLeave={() => setHover(false)}
        style={{ display: "flex", alignItems: "center", gap: 6, width: "100%", overflow: "hidden" }}
      >
        {ticketIdLabel ? <TicketIdChip label={ticketIdLabel} /> : null}
        <span
          title={value}
          style={{
            fontSize: 13.5,
            color: isSubitem ? "var(--ink-2)" : "var(--ink-1)",
            fontWeight: isSubitem ? 400 : 500,
            overflow: "hidden",
            textOverflow: "ellipsis",
            whiteSpace: "nowrap",
            flexShrink: 1,
            minWidth: 0,
            cursor: hasTitle ? "pointer" : undefined,
            fontFamily: "Inter Variable, Inter, system-ui, sans-serif",
          }}
        >
          {value || <span style={{ color: "var(--ink-4)" }}>{t.titleEmpty}</span>}
        </span>
        {/* Spacer — clicks here bubble to CellWrap (open panel / edit when empty). */}
        <span style={{ flex: 1, minWidth: 0, alignSelf: "stretch" }} />
        {ticketCreatedAt ? (
          <TicketAgeChip createdAt={ticketCreatedAt} resolvedAt={ticketResolvedAt} />
        ) : null}
        {commentCount > 0 ? (
          <span
            role="button"
            tabIndex={0}
            onClick={(e) => {
              e.stopPropagation();
              onOpen();
            }}
            onMouseDown={(e) => e.stopPropagation()}
            title={`${commentCount} ${commentCount === 1 ? "Kommentar" : "Kommentare"}`}
            style={{
              display: "inline-flex",
              alignItems: "center",
              gap: 3,
              fontSize: 11,
              fontWeight: 500,
              color: "var(--ink-3)",
              flexShrink: 0,
              cursor: "pointer",
            }}
          >
            <Icon name="comment" size={12} color="var(--ink-4)" />
            {commentCount}
          </span>
        ) : null}
        {/* Hover-only on a titled row (no table-wide noise). But an EMPTY row's primary click
            enters inline edit, never opens the panel — so without this the panel's trash is
            unreachable and the row can't be deleted at all. Show the chip AT REST when empty to
            restore the one obvious open→delete path (and to flag stray blank rows). */}
        {hover || !hasTitle ? (
          <span
            role="button"
            tabIndex={0}
            onClick={(e) => {
              e.stopPropagation();
              onOpen();
            }}
            onMouseDown={(e) => e.stopPropagation()}
            style={{
              display: "inline-flex",
              alignItems: "center",
              gap: 4,
              fontSize: 11,
              fontWeight: 500,
              color: "var(--ink-3)",
              background: "var(--surface)",
              border: "1px solid var(--hairline)",
              borderRadius: 5,
              padding: "2px 7px",
              flexShrink: 0,
              cursor: "pointer",
              fontFamily: "Inter Variable, Inter, system-ui, sans-serif",
            }}
          >
            {t.openChip} <Icon name="arrow" size={10} />
          </span>
        ) : null}
      </div>
    </CellWrap>
  );
}
